import { Device, from } from '@helpers/media';
import { withMotionMax } from '@hoc/withMotionMax';
import useBodyScrollLock from '@hooks/useBodyScrollLock';
import useOnClickOutside from '@hooks/useClickOutside';
import useMedia from '@hooks/useMedia';
import IconZoomIn from '@img/icons/icon-magnifying-glass-with-plus.svg';
import Cross from '@img/icons/icon-x.svg';
import CarouselProvider from '@stories/Components/Carousel/CarouselProvider/CarouselProvider';
import Connected from '@stories/Components/Carousel/Connected/Connected';
import ImageZoomModal from '@stories/Components/Media/ImageZoomModal/ImageZoomModal';
import Link from '@stories/Elements/Forms/Buttons/Link';
import CarouselButton from '@stories/Elements/Media/CarouselButton/CarouselButton';
import ImageTransform from '@tti/image-transform';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import { AnimatePresence } from 'framer-motion';
import React, { FunctionComponent, useState } from 'react';
import { createPortal } from 'react-dom';
import { InfoModalNPIStyles as S } from './InfoModalNPI.styles';

export interface InfoModalNPIProps {
  product: ProductViewModel;
  images?: string[];
  translations: Record<string, string | undefined>;
  isOpen: boolean;
  onClose: () => void;
}

const InfoModalNPI: FunctionComponent<InfoModalNPIProps> = ({
  product,
  images = [],
  translations,
  isOpen,
  onClose,
}) => {
  const isMobile = useMedia([from(Device.Tablet)], [false], true);
  const slidesToShow = useMedia([from(Device.TabletLarge)], [4], 3);

  const contentRef = useBodyScrollLock<HTMLDivElement>(isOpen && isMobile);
  const featureRef = useBodyScrollLock<HTMLDivElement>(isOpen && !isMobile);
  useOnClickOutside(contentRef, () => {
    !showGallery && _onClose();
  });

  const carouselImages = [product.pngListingImage, ...images];
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showGallery, setShowGallery] = useState<boolean>(false);

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') {
      return;
    }

    !showGallery && _onClose();
  };

  const _onClose = (): void => {
    clearAllBodyScrollLocks();
    onClose();
  };

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <AnimatePresence>
      {isOpen && (
        <S.Container>
          {showGallery
            ? createPortal(
                <ImageZoomModal
                  images={carouselImages}
                  initialIndex={activeImageIndex}
                  onClose={() => setShowGallery(false)}
                  hideBackdrop={true}
                />,
                document.body,
              )
            : null}
          <S.Content ref={contentRef} animate={showGallery ? 'hidden' : 'visible'}>
            <S.CrossHolder onClick={_onClose}>
              <Cross />
            </S.CrossHolder>
            <S.HeadingSection>
              <S.Heading>{product.title}</S.Heading>
            </S.HeadingSection>
            <S.ContentSection>
              <S.ProductSection>
                {product.modelCode && <S.Description dangerouslySetInnerHTML={{ __html: product.modelCode }} />}
                {product.articleNumber && <S.AgilityId>{product.articleNumber}</S.AgilityId>}
                {product.systemLogo && product.systemLogo.whiteBg && (
                  <S.SystemLogo
                    src={product.systemLogo.whiteBg}
                    loading="lazy"
                    alt={product.systemLogo.description ? product.systemLogo.description : undefined}
                  />
                )}
                <S.ProductInfo key={product.agilityId}>
                  {carouselImages[activeImageIndex] && (
                    <>
                      <S.ProductImage
                        src={new ImageTransform(carouselImages[activeImageIndex]).width(400).toString()}
                        width="400"
                        height="400"
                      />
                      <S.ZoomControlButton onClick={() => setShowGallery(true)}>
                        <IconZoomIn />
                      </S.ZoomControlButton>
                    </>
                  )}
                </S.ProductInfo>
                {images.length > 1 ? (
                  <S.ProductImages>
                    <CarouselProvider count={images.length} paddingOffset={0} pageChildren={1}>
                      <Connected>
                        {(carousel) => (
                          <>
                            {images.length > slidesToShow && (
                              <CarouselButton
                                direction="left"
                                disabled={carousel.activeIndex === 0}
                                theme="white"
                                hideLine={true}
                                onClick={(event) => {
                                  event.preventDefault();
                                  carousel?.goToPrev();
                                }}
                                hideWhenDisabled={false}
                              />
                            )}
                            <S.Track
                              data-center={images.length > slidesToShow}
                              trackMutations={true}
                              enableCarousel={true}
                            >
                              {carouselImages.map((image, i) => (
                                <S.ProductImageWrapper
                                  key={`InfoModalNPI_productFeatures_${i}`}
                                  data-active={i == activeImageIndex}
                                >
                                  <S.ProductImage
                                    src={new ImageTransform(image).width(64).toString()}
                                    width="64"
                                    height="64"
                                    onClick={() => setActiveImageIndex(i)}
                                  />
                                </S.ProductImageWrapper>
                              ))}
                            </S.Track>
                            {images.length > slidesToShow && (
                              <CarouselButton
                                direction="right"
                                theme="white"
                                hideLine={true}
                                disabled={carousel.activeIndex + slidesToShow >= images?.length}
                                onClick={(event) => {
                                  event.preventDefault();
                                  carousel?.goToNext();
                                }}
                                hideWhenDisabled={false}
                              />
                            )}
                          </>
                        )}
                      </Connected>
                    </CarouselProvider>
                  </S.ProductImages>
                ) : null}
              </S.ProductSection>
              <S.DetailsSection ref={featureRef}>
                <S.FeatureSection>
                  <S.FeatureTitle>{translations['custom.product-info-modal.features']}</S.FeatureTitle>
                  <S.FeatureList>
                    {product.productFeatures.map((feature, i) => (
                      <S.Feature key={`InfoModalNPI_productFeatures_${i}`}>{feature}</S.Feature>
                    ))}
                  </S.FeatureList>
                </S.FeatureSection>
                {product.isPublished && product.url && (
                  <S.ButtonSection>
                    <Link
                      href={product.url}
                      theme="red"
                      text={translations['custom.product-info-modal.view-product']}
                      size="small"
                    />
                  </S.ButtonSection>
                )}
              </S.DetailsSection>
            </S.ContentSection>
          </S.Content>
        </S.Container>
      )}
    </AnimatePresence>
  );
};

export default withMotionMax(InfoModalNPI);
